import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  Edit3,
  Eye,
  Flag,
  MapPin,
  Pause,
  PlayCircle,
  Trash2,
  TrendingUp,
  User,
  X,
} from 'lucide-react';
import React from 'react';
import { Task, TaskStatus } from '../../types';

interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onView?: (task: Task) => void;
  onTrackProgress?: (task: Task) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onEdit,
  onDelete,
  onStatusChange,
  onView,
  onTrackProgress,
}) => {
  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <PlayCircle className="h-4 w-4 text-brand-primary" />;
      case 'on_hold':
        return <Pause className="h-4 w-4 text-yellow-600" />;
      case 'review':
        return <Clock className="h-4 w-4 text-purple-600" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return 'text-green-700 bg-green-100 border-green-200';
      case 'in_progress':
        return 'text-brand-700 bg-brand-100 border-brand-200';
      case 'review':
        return 'text-purple-700 bg-purple-100 border-purple-200';
      case 'on_hold':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'cancelled':
        return 'text-red-700 bg-red-100 border-red-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'text-red-700 bg-red-100 border-red-200';
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'low':
        return 'text-green-700 bg-green-100 border-green-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const formatDate = (date: Date | string) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isOverdue =
    task.dueDate &&
    new Date(task.dueDate) < new Date() &&
    task.status !== 'completed' &&
    task.status !== 'cancelled';
  const isDueSoon =
    task.dueDate &&
    !isOverdue &&
    new Date(task.dueDate) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) &&
    task.status !== 'completed' &&
    task.status !== 'cancelled';

  const getProgressPercentage = () => {
    if (task.status === 'completed') return 100;
    if (task.status === 'in_progress') return task.progress || 50;
    if (task.status === 'review') return 90;
    return 0;
  };

  const handleStatusChange = (newStatus: TaskStatus) => {
    if (newStatus !== task.status) {
      onStatusChange(task.id, newStatus);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Priority indicator */}
      <div
        className={`w-full h-1 rounded-t-lg mb-4 ${
          task.priority === 'critical'
            ? 'bg-red-600'
            : task.priority === 'high'
              ? 'bg-red-400'
              : task.priority === 'medium'
                ? 'bg-yellow-400'
                : 'bg-green-400'
        }`}
      />

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{task.title}</h3>
          <p className="text-sm text-gray-600 line-clamp-2">{task.description}</p>
        </div>

        <div className="flex items-center space-x-1 ml-2">
          {onView && (
            <button
              onClick={() => onView(task)}
              className="p-1 text-gray-400 hover:text-indigo-600 rounded transition-colors"
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {onTrackProgress && (
            <button
              onClick={() => onTrackProgress(task)}
              className="p-1 text-gray-400 hover:text-green-600 rounded transition-colors"
              title="Track Progress"
            >
              <TrendingUp className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onEdit(task)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title="Edit Task"
          >
            <Edit3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(task.id)}
            className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
            title="Delete Task"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Status and Category */}
      <div className="flex items-center space-x-2 mb-4">
        <div className="flex items-center space-x-1">
          {getStatusIcon(task.status)}
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(task.status)}`}
          >
            {task.status?.charAt(0).toUpperCase() + task.status?.slice(1).replace('_', ' ')}
          </span>
        </div>
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}
        >
          <Flag className="h-3 w-3 mr-1" />
          {task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1)}
        </span>
      </div>

      {/* Task Details */}
      <div className="space-y-2 mb-4">
        {task.assignedTo && (
          <div className="flex items-center text-sm text-gray-600">
            <User className="h-4 w-4 mr-2 text-gray-400" />
            <span>
              {task.assignedTo.firstName} {task.assignedTo.lastName}
            </span>
          </div>
        )}

        {task.dueDate && (
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
            <span
              className={
                isOverdue
                  ? 'text-red-600 font-medium'
                  : isDueSoon
                    ? 'text-yellow-600 font-medium'
                    : ''
              }
            >
              Due {formatDate(task.dueDate)}
            </span>
            {isOverdue && <span className="ml-2 text-xs text-red-600 font-medium">(Overdue)</span>}
            {isDueSoon && (
              <span className="ml-2 text-xs text-yellow-600 font-medium">(Due Soon)</span>
            )}
          </div>
        )}

        {task.estimatedHours && (
          <div className="flex items-center text-sm text-gray-600">
            <Clock className="h-4 w-4 mr-2 text-gray-400" />
            <span>{task.estimatedHours}h estimated</span>
            {task.actualHours && <span className="ml-2">• {task.actualHours}h actual</span>}
          </div>
        )}

        {task.location && (
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
            <span>{task.location}</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium">{getProgressPercentage()}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              task.status === 'completed'
                ? 'bg-green-500'
                : task.status === 'in_progress'
                  ? 'bg-brand-500'
                  : task.status === 'review'
                    ? 'bg-purple-500'
                    : 'bg-gray-400'
            }`}
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
      </div>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {task.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
            >
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
              +{task.tags.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center space-x-2">
          {task.status !== 'completed' && (
            <button
              onClick={() => handleStatusChange('completed')}
              className="text-xs text-green-600 hover:text-green-700 font-medium"
            >
              Mark Complete
            </button>
          )}
          {task.status === 'todo' && (
            <button
              onClick={() => handleStatusChange('in_progress')}
              className="text-xs text-brand-primary hover:text-brand-700 font-medium"
            >
              Start Task
            </button>
          )}
          {task.status === 'in_progress' && (
            <button
              onClick={() => handleStatusChange('on_hold')}
              className="text-xs text-yellow-600 hover:text-yellow-700 font-medium"
            >
              Put on Hold
            </button>
          )}
        </div>

        <div className="text-xs text-gray-500">
          {task.category?.charAt(0).toUpperCase() + task.category?.slice(1).replace('_', ' ')}
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
